import type { ChangeEvent, FC, FocusEvent, ReactElement } from 'react'
import { useRef } from 'react'

import type { WhiteLabel } from '@skytech/types'

import { ErrorMessage } from '../ErrorMessage'
import { Label } from '../Label'

import './styles.scss'

export type InputProps = {
  id: string
  isFullWidth?: boolean
  className?: string
  wrapperClassName?: string
  label?: string | ReactElement
  ariaLabel?: string
  value?: string | number
  type?: string
  inputMode?: 'search' | 'text' | 'email' | 'tel' | 'url' | 'none' | 'numeric' | 'decimal'
  pattern?: string
  error?: string
  customError?: string | ReactElement
  touched?: boolean
  placeholder?: string
  marginBottom?: number
  marginRight?: number
  minWidth?: number
  capitalizeLabel?: boolean
  RightElement?: ReactElement
  LeftElement?: ReactElement
  maxLength?: number
  max?: string
  min?: string
  autoComplete?: string
  required?: boolean
  isDisabled?: boolean
  isAllTextSelectedOnFocus?: boolean
  useDefaultWhiteLabel?: boolean // If true, use the default white label instead of the user's
  onClickRightElement?: () => void
  onBlur?: (e: FocusEvent<unknown, Element>) => void
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
}

export const Input: FC<InputProps> = ({
  id,
  value,
  placeholder,
  className = '',
  wrapperClassName = '',
  label,
  ariaLabel,
  type,
  inputMode,
  pattern,
  error,
  customError,
  RightElement,
  LeftElement,
  marginBottom,
  marginRight,
  capitalizeLabel,
  touched,
  autoComplete,
  maxLength,
  max,
  min,
  minWidth,
  isFullWidth,
  isDisabled,
  isAllTextSelectedOnFocus,
  required,
  useDefaultWhiteLabel = false,
  onChange,
  onBlur,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null)

  // todo: replace with the org from url when ready
  const whiteLabel = useDefaultWhiteLabel ? 'control' : (localStorage.getItem('org') as WhiteLabel)

  const errorClass = error && touched ? 'has-error' : ''
  const fullWidthClass = isFullWidth ? 'is-full-width' : ''

  return (
    <div
      className={`ui-input-wrapper ${wrapperClassName} ${isDisabled ? 'is-disabled' : ''}`}
      style={{ marginBottom: marginBottom || 0, marginRight: marginRight || 0, minWidth }}
    >
      {label && (
        <Label capitalizeLabel={capitalizeLabel} htmlFor={id}>
          {label}
        </Label>
      )}

      <div className="relative">
        {LeftElement && (
          <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-lg text-zinc-400">
            {LeftElement}
          </div>
        )}

        <input
          ref={inputRef}
          id={id}
          aria-label={ariaLabel}
          className={`ui-input ${errorClass} ${className} ${fullWidthClass} ${whiteLabel} ${
            LeftElement ? 'pl-14' : ''
          }`}
          type={type}
          inputMode={inputMode}
          pattern={pattern}
          value={value}
          placeholder={placeholder}
          disabled={isDisabled}
          autoComplete={autoComplete}
          required={required}
          min={min}
          max={max}
          maxLength={maxLength}
          onFocus={() => {
            if (isAllTextSelectedOnFocus) inputRef.current?.select()
          }}
          onChange={onChange}
          // Crucial for setting the "touched" fields in the form
          onBlur={onBlur}
        />

        {RightElement && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-lg">{RightElement}</div>
        )}
      </div>

      {error && touched && <ErrorMessage>{error}</ErrorMessage>}
      {customError && <ErrorMessage>{customError}</ErrorMessage>}
    </div>
  )
}
